import { TFunction } from "i18next";
import Grid from '@mui/material/Grid2';
import Card from "../../../components/card/Card";
import {
  cardContentStyles,
  cardHeaderStyles,
  deviceGridActiveStyles,
  deviceGridSessionItemStyles,
  deviceListItemDateStyles,
  deviceListItemSessionText,
  deviceListItemStyles,
  deviceListStyles,
  deviceSessionBrowserInfoStyles,
  StyledActiveNumber,
  StyledActiveSessionsBox,
  StyledImage,
  ViewAllButton
} from "../styles";
import { DeviceRepresentation } from "../../../api/representations";
import Box from "@mui/material/Box/Box";
import { Divider, List, ListItem, ListItemText, Typography, useMediaQuery, useTheme } from "@mui/material";
import { formatDate } from "../../../utils/formatDate";
import React, { useMemo } from "react";
import { StyledListBox } from "../../../components/datalist/styles";
import { StyledChip } from "../../../components/styles";
import { useNavigate } from "react-router-dom";
import { useMenu } from "../../../providers/MenuProvider";
import {Spinner} from "@keycloak/keycloak-ui-shared";
import getSoftwareName from "../../../utils/getSoftwareName";

interface DeviceActivityWidgetProps {
    t: TFunction;
    devices?: DeviceRepresentation[];
    resourceUrl: string;
    loading: boolean;
}

export default function DeviceActivityWidget({ t, devices, resourceUrl, loading }: DeviceActivityWidgetProps) {
    const navigate = useNavigate();
    const theme = useTheme();
    const { isOpen } = useMenu();
    const isUpLg = useMediaQuery(theme.breakpoints.up('lg'));
    const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
    const isDownXl = useMediaQuery(theme.breakpoints.down('xl'));

    const action = devices?.length && (
            <ViewAllButton component="button" onClick={() => navigate('account-security/device-activity')}>{t('viewAll')}</ViewAllButton>);

    const aggregateSessions = useMemo(() => {
        return devices?.flatMap(device => {
            return device.sessions.map(session => ({
                ...session,
                os: device.os,
                osVersion: device.osVersion,
                device: device.device,
                lastAccess: device.lastAccess,
                mobile: device.mobile,
            }));
        }) || [];
    }, [devices]);

    const deviceActivityTitle = (isUpLg && aggregateSessions.length === 1) || isDownLg || isDownXl && isOpen ? ` | ${aggregateSessions.length}` : '';
  
    return (
        <Card title={`${t('deviceActivity')}${deviceActivityTitle}`} action={action} sxContent={cardContentStyles} sxHeader={cardHeaderStyles}>
            { loading ?
              <Spinner /> :
              (<Grid container spacing={aggregateSessions.length > 1 ? 3 : 0}>
                <Grid size="auto" sx={{...deviceGridActiveStyles, display: { xs: 'none', lg: (isOpen && isDownXl) ? 'none' : 'flex' }}}>
                    {aggregateSessions.length > 1 && (<Box textAlign="center">
                      <StyledActiveSessionsBox>
                        <Box marginBottom="13px">
                            <StyledActiveNumber>{aggregateSessions.length}</StyledActiveNumber>
                            <Typography variant="body2">{t('activeSessions')}</Typography>
                        </Box>
                      </StyledActiveSessionsBox>
                    </Box>)}
                </Grid>
                <Grid size="grow">
                    <List sx={deviceListStyles}>
                        {aggregateSessions.slice(0, 3).map((session, indx, arr) => (
                            <React.Fragment key={session.lastAccess + indx}>
                                <ListItem sx={deviceListItemStyles}> 
                                  <StyledListBox>
                                    <Grid container alignItems="start" spacing={1}>
                                      <Grid sx={deviceGridSessionItemStyles}>
                                        <StyledImage sx={{ marginTop: '5px' }} src={`${resourceUrl}/images/software/${getSoftwareName(session.os)}.svg`} alt={session.os} />
                                      </Grid>
                                      <Grid container size="grow" justifyContent="space-between">
                                        <Grid>
                                          <ListItemText sx={deviceListItemSessionText}>
                                            <Box sx={deviceSessionBrowserInfoStyles}>{session.browser}/{session.os}</Box>
                                            {session.current && <StyledChip size="small" label={t('currentSession')} color="success" variant="outlined" />}
                                          </ListItemText>
                                        </Grid>
                                        <Grid>
                                          <ListItemText
                                            sx={deviceListItemDateStyles}
                                          >
                                            {t('lastLogin')}: {formatDate(new Date(session.lastAccess * 1000))}
                                          </ListItemText>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </StyledListBox>    
                                </ListItem>
                                {indx < arr.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </Grid>
            </Grid>)}
        </Card>
    );
}